import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Ticket } from "@app2/type-defs/ticket/ticket-types";
import * as _ from "underscore";

@Component({
    selector: "ticket-embedded-list",
    template: `
        <table *dsLoading="loading" class="hover-table full-width">
            <thead *ngIf="showHeaders">
            <tr>
                <th class="hover-tab"></th>
                <th *ngIf="canSelectMultipleTickets" class="icon-cell center"></th>
                <th>
                    {{ "NAME" | localize }}
                </th>
                <th *ngIf="showOwner"
                    class="short-cell center">
                    {{ "OWNER" | localize }}
                </th>
                <th class="short-cell center">
                    {{ "STATE" | localize }}
                </th>
                <th *ngIf="showDueDate"
                    class="timestamp-cell center">
                    {{ "DUE_DATE" | localize }}
                </th>
                <th *ngIf="showCreatedBy"
                    class="timestamp-cell center">
                    {{ "CREATED_ON" | localize }}
                </th>
                <th *ngIf="showUpdatedBy"
                    class="timestamp-cell center">
                    {{ "LAST_MODIFIED_ON" | localize }}
                </th>
                <th *ngIf="showActions"
                    class="icon-cell">
                </th>
            </tr>
            </thead>
            <tbody>
            <tr *ngFor="let ticket of tickets"
                class="declutter"
                [class.click-target]="canSelectTickets"
                (click)="(canSelectTickets || canSelectMultipleTickets) && toggleSelected(ticket)"
                [class.selected-row]="isSelected(ticket)">
                <td class="hover-tab"></td>
                <td *ngIf="canSelectMultipleTickets" class="icon-cell"
                    test-class="column-select-checkbox">
                    <i class="fal fa-square-check" *ngIf="isSelected(ticket)"></i>
                    <i class="fal fa-square" *ngIf="!isSelected(ticket)"></i>
                </td>
                <td class="link-cell">
                    <span class="de-emphasize slug-id">
                        {{ ticket.slugId }}
                    </span>
                    <a *ngIf="canNavigateToTickets"
                       class="task-title"
                       [dsRouterLink]="['/tickets', ticket.slugName, ticket.slugId]">
                        {{ ticket.summary }}<br/>
                    </a>
                    <span class="task-title" *ngIf="!canNavigateToTickets">
                        {{ ticket.summary }}<br/>
                    </span>
                </td>
                <td class="timestamp-cell center"
                    *ngIf="showOwner">
                    <person-name [id]="ticket.ownedBy"
                                 [isSystem]="!ticket.ownedBy"></person-name>
                </td>
                <td class="short-cell center">
                    <ticket-state-badge [state]="ticket.state"
                                        [closedOn]="ticket.closedOn"
                                        [closedException]="ticket.taskData?.closedException">
                    </ticket-state-badge>
                </td>
                <td class="timestamp-cell center"
                    *ngIf="showDueDate">
                    <due-date-badge *ngIf="ticket.dueOn"
                                    [dueOn]="ticket.dueOn"
                                    [closedOn]="ticket.closedOn">
                    </due-date-badge>
                </td>
                <td class="timestamp-cell center"
                    *ngIf="showCreatedBy">
                    <person-with-date [isoString]="ticket.createdOn"
                                      [personId]="ticket.createdBy"
                                      [isSystem]="!ticket.createdBy"></person-with-date>
                </td>
                <td class="timestamp-cell center"
                    *ngIf="showUpdatedBy">
                    <person-with-date [isoString]="ticket.updatedOn"
                                      [personId]="ticket.updatedBy"
                                      [isSystem]="!ticket.updatedBy"></person-with-date>
                </td>
                <td class="text-center icon-cell"
                    *ngIf="showActions">
                    <i *ngIf="deleting === ticket.slugId" class="fa fa-spinner fa-spin"></i>
                    <button *ngIf="deleting !== ticket.slugId"
                            class="link inline-delete show-on-hover alert"
                            [attr.test-id]="'ticket-delete-button-' + ticket.slugId"
                            (click)="deleted.emit(ticket)">
                        <i class="ion-trash-a"></i>
                    </button>
                </td>
            </tr>
            <tr *ngIf="tickets.length === 0">
                <td class="no-data-text" [attr.colspan]="numColumns()">
                    {{ "TICKETS_LIST_EMPTY" | localize }}
                </td>
            </tr>
            </tbody>
        </table>
    `,
    styles: [`
      .link-cell {
        .slug-id {
          padding-bottom: 0 !important;
        }

        .task-title {
          padding-top: 4px !important;
        }
      }
    `],
})
export class TicketEmbeddedListComponent {
    @Input() tickets: Ticket[];
    @Input() loading: boolean;
    // Optional inputs
    @Input() showHeaders = false;
    @Input() canSelectTickets = false;
    @Input() canSelectMultipleTickets = false;
    @Input() canNavigateToTickets = true;
    @Input() showOwner = false;
    @Input() showDueDate = false;
    @Input() showCreatedBy = false;
    @Input() showUpdatedBy = false;
    @Input() deleting: number; // Slug Id

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onSelected = new EventEmitter<Ticket[]>();
    @Output() deleted = new EventEmitter<Ticket>();

    private selectedTickets: { [key: string]: Ticket } = {};

    constructor() {
    }

    get showActions(): boolean {
        return this.deleted.observers.length > 0;
    }

    toggleSelected(ticket: Ticket) {
        if (this.selectedTickets[ticket.slugId!]) {
            delete this.selectedTickets[ticket.slugId!];
        } else if (this.canSelectMultipleTickets) {
            this.selectedTickets[ticket.slugId!] = ticket;
        } else {
            this.selectedTickets = { [ticket.slugId!]: ticket };
        }
        this.onSelected.emit(_.values(this.selectedTickets));
    }

    isSelected(ticket: Ticket) {
        return !!this.selectedTickets[ticket.slugId!];
    }

    numColumns(): number {
        let numCols = 3;
        if (this.showOwner) {
            numCols++;
        }
        if (this.showDueDate) {
            numCols++;
        }
        if (this.showCreatedBy) {
            numCols++;
        }
        if (this.showUpdatedBy) {
            numCols++;
        }
        if (this.showActions) {
            numCols++;
        }
        return numCols;
    }
}
